// component-build-set-configurator.js (v2)
//
// Build-a-Set v2 Custom Element. Owns the full page state:
//   - Browse: activeCollection, query, sort
//   - Configurator (Batch C): selectedProductId, cfgSize, cfgColor, cfgQty
//   - Set & summary (Batch D): set[], lastKey, pulse, celebrate
//
// Architecture:
//   Sidecar JSON (rendered by sections/build-set.liquid) carries the full
//   catalog (collections x products x variants) and the tier table. The CE
//   reads it once, then operates entirely client-side. Product cards are
//   pre-rendered by Liquid; the CE just toggles visibility + reorders for
//   filter / search / sort.
//
// Batch B scope: Browse view only.
//   - Collection chip rail (active state + horizontal scroll w/ arrows)
//   - All-collections modal
//   - Search input (debounced) + sort dropdown
//   - Card show/hide based on filters
//   - Step bar state updates
//
// Source: BuildSetPage.jsx:494-560 + sub-components.

(function () {
  if (window.customElements && window.customElements.get('build-set-configurator')) return;

  function debounce(fn, ms) {
    var t = null;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  function copy(key, fallback) {
    return (window.LunchedBuildSetCopy && window.LunchedBuildSetCopy[key]) || fallback;
  }

  function peso(amountCents) {
    // amountCents is Shopify "money" cents. Use the same formatter as everywhere else.
    if (window.Shopify && Shopify.formatMoney) return Shopify.formatMoney(amountCents);
    // PHP peso sign U+20B1 as Unicode escape so source stays ASCII.
    return '₱' + Math.round(amountCents / 100).toLocaleString('en-US');
  }

  // Locate the color-like option position (1 or 2) by looking at the
  // product's option names (e.g. "Color", "Colour", "Colorway"). The
  // remaining position is treated as "size" - it might really be Size,
  // but it can also be Type, Length, etc. Products with only a color-
  // like option have size=0; those with only a non-color option have
  // color=0. `_findVariant` and the render helpers below key off this
  // shape so the pickers match the PDP (which iterates
  // product.options_with_values by name, not by fixed position).
  function optionPositions(product) {
    var names = (product && product.options) || [];
    var color = 0, size = 0;
    for (var i = 0; i < names.length && i < 2; i++) {
      var lower = String(names[i] || '').toLowerCase();
      if (lower.indexOf('color') !== -1 || lower.indexOf('colour') !== -1) {
        color = i + 1;
        break;
      }
    }
    for (var j = 0; j < names.length && j < 2; j++) {
      if ((j + 1) === color) continue;
      size = j + 1;
      break;
    }
    return { color: color, size: size };
  }

  function variantSize(variant, product) {
    var pos = optionPositions(product).size;
    return pos ? variant['option' + pos] : null;
  }

  function variantColor(variant, product) {
    var pos = optionPositions(product).color;
    return pos ? variant['option' + pos] : null;
  }

  function uniqSize(product) {
    var pos = optionPositions(product).size;
    if (!pos) return [];
    var key = 'option' + pos;
    var seen = {}, out = [];
    (product.variants || []).forEach(function (v) {
      if (v[key] != null && !seen[v[key]]) { seen[v[key]] = true; out.push(v[key]); }
    });
    return out;
  }

  function uniqColor(product) {
    var pos = optionPositions(product).color;
    if (!pos) return [];
    var key = 'option' + pos;
    var seen = {}, out = [];
    (product.variants || []).forEach(function (v) {
      if (v[key] != null && !seen[v[key]]) { seen[v[key]] = true; out.push(v[key]); }
    });
    return out;
  }

  function sizeDescriptorFor(sizeValue) {
    // sizeValue like "300 ml" - extract leading number, look up locale-keyed
    // descriptor in LunchedBuildSetCopy. Returns '' if no match.
    if (!sizeValue) return '';
    var m = String(sizeValue).match(/(\d+)/);
    if (!m) return '';
    return copy('size_desc_' + m[1], '');
  }

  function badgeFor(product) {
    // Returns { label, tone } from product metafield or tags. The Liquid card
    // already renders the same logic server-side; this is the JS mirror for
    // the configurator hero badge.
    var slug = product && product.badge_slug;
    if (!slug) return null;
    switch (String(slug).toLowerCase()) {
      case 'bestseller': return { label: 'Bestseller', tone: 'sun' };
      case 'new':        return { label: 'New', tone: 'magenta' };
      case 'limited':    return { label: 'Limited', tone: 'tomato' };
      default:           return null;
    }
  }

  var ATTR_ESCAPE = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  function escapeAttr(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return ATTR_ESCAPE[c]; });
  }
  function escapeText(s) {
    return String(s == null ? '' : s).replace(/[&<>]/g, function (c) { return ATTR_ESCAPE[c]; });
  }

  // Mirror of snippets/brand-color-token.liquid for JS-time color resolution.
  // The Liquid version is canonical; this string-match table mirrors it for
  // dynamically-rendered swatches inside the configurator.
  function colorToCssVar(name) {
    var n = String(name == null ? '' : name).toLowerCase();
    if (n.indexOf('tomato') !== -1) return 'var(--tomato-400)';
    if (n.indexOf('sky') !== -1)    return 'var(--blue-300)';
    if (n.indexOf('blue') !== -1)   return 'var(--blue-400)';
    if (n.indexOf('sun') !== -1)    return 'var(--sun-400)';
    if (n.indexOf('berry') !== -1 || n.indexOf('magenta') !== -1 || n.indexOf('pink') !== -1) {
      return 'var(--magenta-400)';
    }
    if (n.indexOf('cream') !== -1 || n.indexOf('oat') !== -1) return 'var(--cream-base)';
    return 'var(--cream-200)';
  }

  class BuildSetConfigurator extends HTMLElement {
    constructor() {
      super();
      this._state = {
        activeCollection: '',  // '' = all
        query: '',
        sort: 'featured',
        selectedProductId: null,
        cfgSize: null,
        cfgColor: null,
        cfgQty: 1,
        set: [],
        lastKey: null,
      };
      this._prevPct = 0;
      this._toastTimer = null;
      this._onClick = this._onClick.bind(this);
      this._onMousedown = this._onMousedown.bind(this);
      this._onInputDebounced = debounce(this._onInput.bind(this), 80);
      this._onInput = this._onInput.bind(this);
      this._onChange = this._onChange.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._updateChipRailOverflow = this._updateChipRailOverflow.bind(this);
      this._onCartUpdated = this._onCartUpdated.bind(this);
      this._onCardMouseover = this._onCardMouseover.bind(this);
      this._onCardMouseout = this._onCardMouseout.bind(this);
      this._onCardTouchStart = this._onCardTouchStart.bind(this);
      this._onCardTouchMove = this._onCardTouchMove.bind(this);
      this._onCardTouchEnd = this._onCardTouchEnd.bind(this);
      this._basSwipeStartX = 0;
      this._basSwipeStartY = 0;
      this._basSwipeCard = null;
      this._basSwipeAxis = null;
      // matchMedia gate for hover-gallery: touch devices simulate mouseover
      // on tap which would cause a jitter, so skip attaching hover handlers
      // there. Same pattern as the PDP hover-preview + product-card CE.
      this._basSupportsHover = typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    }

    /**
     * Cart contents count toward the available-to-add cap (Shopify's /cart/add.js
     * adds to existing line qty, so set qty + cart qty cannot exceed stock).
     * When the cart changes via PDP / drawer / external mutation, re-clamp the
     * current cfgQty against the new cap and re-render the CTA + hint.
     */
    _onCartUpdated() {
      if (this._state.selectedProductId == null) return;
      this._setCfgQty(this._state.cfgQty);
    }

    // ---- BAS card hover-gallery ------------------------------------------
    // Update .bas-card__media[data-idx]. CSS attribute-selector ladder
    // handles the actual opacity swap; JS just flips the data attribute.
    _setBasCardIdx(media, next) {
      if (!media) return;
      media.dataset.idx = String(next);
    }

    _onCardMouseover(e) {
      var card = e.target.closest('.bas-card');
      if (!card || !this.contains(card)) return;
      // Ignore transits between descendants of the same card.
      if (card.contains(e.relatedTarget)) return;
      var media = card.querySelector('.bas-card__media');
      if (!media) return;
      var count = parseInt(media.dataset.imageCount, 10) || 1;
      if (count < 2) return;
      this._setBasCardIdx(media, 1);
    }

    _onCardMouseout(e) {
      var card = e.target.closest('.bas-card');
      if (!card || !this.contains(card)) return;
      if (card.contains(e.relatedTarget)) return;
      var media = card.querySelector('.bas-card__media');
      if (!media) return;
      this._setBasCardIdx(media, 0);
    }

    // Touch equivalents of the desktop hover-arrow cycle. Leftward finger
    // drag = next image, rightward = previous. Wraps via `(curr + dir + N) % N`
    // just like the arrow branch of _onClick. preventDefault on touchend
    // suppresses the card <button>'s select-product click for this gesture.
    // Axis lock on touchmove: once the finger commits to a horizontal
    // gesture (first ~10px), preventDefault every subsequent touchmove so
    // the page doesn't scroll vertically at the same time. Vertical intent
    // leaves scroll behavior default and skips image cycling.
    _onCardTouchStart(e) {
      if (e.touches.length !== 1) return;
      var card = e.target.closest('.bas-card');
      if (!card || !this.contains(card)) return;
      var media = card.querySelector('.bas-card__media');
      if (!media) return;
      var count = parseInt(media.dataset.imageCount, 10) || 1;
      if (count < 2) return;
      this._basSwipeStartX = e.touches[0].clientX;
      this._basSwipeStartY = e.touches[0].clientY;
      this._basSwipeCard = card;
      this._basSwipeAxis = null;
    }

    _onCardTouchMove(e) {
      if (!this._basSwipeCard) return;
      var dx = e.touches[0].clientX - this._basSwipeStartX;
      var dy = e.touches[0].clientY - this._basSwipeStartY;
      if (this._basSwipeAxis === null) {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
        this._basSwipeAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      if (this._basSwipeAxis === 'x') e.preventDefault();
    }

    _onCardTouchEnd(e) {
      if (!this._basSwipeCard) return;
      var card = this._basSwipeCard;
      this._basSwipeCard = null;
      if (this._basSwipeAxis !== 'x') return;
      var dx = e.changedTouches[0].clientX - this._basSwipeStartX;
      if (Math.abs(dx) < 40) return;
      e.preventDefault();
      var media = card.querySelector('.bas-card__media');
      if (!media) return;
      var count = parseInt(media.dataset.imageCount, 10) || 1;
      var dir = dx < 0 ? 1 : -1;
      var curr = parseInt(media.dataset.idx, 10) || 0;
      this._setBasCardIdx(media, (curr + dir + count) % count);
    }

    connectedCallback() {
      var sidecar = this.querySelector('script[data-build-set-config]');
      if (!sidecar) {
        console.warn('[build-set] config sidecar missing; aborting init');
        return;
      }
      try {
        this._config = JSON.parse(sidecar.textContent);
      } catch (e) {
        console.error('[build-set] config JSON parse failed', e);
        return;
      }
      if (!this._config || !Array.isArray(this._config.collections)) {
        console.warn('[build-set] config sidecar malformed; aborting init');
        return;
      }

      // Flatten catalog (dedup by id) for sort + product lookup.
      this._catalog = this._buildCatalog(this._config.collections);

      this.addEventListener('click', this._onClick);
      this.addEventListener('mousedown', this._onMousedown);
      this.addEventListener('input', this._onInput);
      this.addEventListener('change', this._onChange);
      /* Listen for the qty-stepper CE's debounced change event so the shared
         quantity component (snippets/qty-stepper.liquid) drives BAS state
         updates identically to the PDP. Bubbles to `this` from the nested
         <qty-stepper> element. */
      this.addEventListener('qty-stepper:change', this._onQtyStepperChange.bind(this));
      document.addEventListener('keydown', this._onKeydown);
      document.addEventListener('cart:updated', this._onCartUpdated);
      if (this._basSupportsHover) {
        // mouseover/mouseout bubble (unlike mouseenter/mouseleave), so we
        // can delegate at the CE root and catch every .bas-card without
        // per-card init. Guard via relatedTarget to fire only on real
        // card-boundary crossings (not on internal element transits).
        this.addEventListener('mouseover', this._onCardMouseover);
        this.addEventListener('mouseout', this._onCardMouseout);
      } else {
        // Touch equivalent — delegated swipe detection on the CE root.
        // touchmove is non-passive so we can preventDefault to axis-lock.
        this.addEventListener('touchstart', this._onCardTouchStart, { passive: true });
        this.addEventListener('touchmove', this._onCardTouchMove, { passive: false });
        this.addEventListener('touchend', this._onCardTouchEnd);
      }

      this._setupChipRail();
      this._applyFilter();
      this._applySort();
      this._renderSummary();
      this._updateStep();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('mousedown', this._onMousedown);
      this.removeEventListener('input', this._onInput);
      this.removeEventListener('change', this._onChange);
      document.removeEventListener('keydown', this._onKeydown);
      document.removeEventListener('cart:updated', this._onCartUpdated);
      if (this._basSupportsHover) {
        this.removeEventListener('mouseover', this._onCardMouseover);
        this.removeEventListener('mouseout', this._onCardMouseout);
      } else {
        this.removeEventListener('touchstart', this._onCardTouchStart);
        this.removeEventListener('touchmove', this._onCardTouchMove);
        this.removeEventListener('touchend', this._onCardTouchEnd);
      }
      if (this._railEl) {
        this._railEl.removeEventListener('scroll', this._updateChipRailOverflow);
      }
      window.removeEventListener('resize', this._updateChipRailOverflow);
    }

    _buildCatalog(collections) {
      var seen = Object.create(null);
      var out = [];
      collections.forEach(function (c) {
        (c.products || []).forEach(function (p) {
          // Phase 10: exclude orphan-archived products from the selectable pool
          if (p.orphan_archived) return;
          var id = String(p.id);
          if (seen[id]) return;
          seen[id] = true;
          out.push(p);
        });
      });
      return out;
    }

    // ---- event routing ----------------------------------------------------

    /* Suppress the browser's default "start text selection" behavior
       when the merchant clicks a .bas-card__nav arrow or a variant-image
       swatch. Without this, Chrome/Safari can interpret the mousedown-
       inside-card as the start of a selection range that extends to
       the card title below on release, briefly highlighting the title
       (reported 2026-07-12, matches the fix in component-product-card.js). */
    _onMousedown(e) {
      var navEl = e.target.closest('[data-action="bas-card-nav"], [data-action="bas-card-swatch"]');
      if (navEl) e.preventDefault();
    }

    /* Swap .bas-card__gallery layers to the clicked variant's resolved
       image set. Payload is emitted per BAS card by
       snippets/build-set-product-card.liquid as
       <script type="application/json" data-variant-images> when the card
       is in variant-image-thumbnail mode. Mirrors _swapGalleryTo() in
       assets/component-product-card.js. */
    _swapBasCardGallery(card, variantId) {
      if (!card || !variantId) return;

      /* Gallery-image swap (only when the card was rendered with a
         data-variant-images payload). Color-option products (Best Lunch
         Box Ever, Dipper, Toy Trio) DON'T emit this payload today - their
         swatches iterate color values, not variant images. For those
         cards, we skip the image swap and only update aria-pressed +
         data-sold-out below (2026-07-14 - previously we `return`ed
         early on missing payload and the swatch never registered as
         pressed, and the sold-out badge never appeared). */
      var payloadEl = card.querySelector('script[type="application/json"][data-variant-images]');
      if (payloadEl) {
        var payload = null;
        try {
          payload = JSON.parse(payloadEl.textContent || '{}');
        } catch (err) {
          console.error('[bas-card] variant-images payload malformed', err);
        }
        var images = payload && payload[variantId];
        if (images && images.length) {
          var media = card.querySelector('.bas-card__media');
          var gallery = media && media.querySelector('.bas-card__gallery');
          if (gallery) {
            var layers = Array.prototype.slice.call(gallery.querySelectorAll('.bas-card__image'));
            var newCount = Math.min(images.length, 5);
            for (var i = 0; i < newCount; i++) {
              var layer = layers[i];
              if (!layer) {
                layer = document.createElement('div');
                layer.className = 'bas-card__image' + (i === 0 ? ' bas-card__image--hero' : '');
                layer.setAttribute('data-position', String(i));
                var img = document.createElement('img');
                img.className = 'bas-card__img';
                img.loading = 'lazy';
                img.decoding = 'async';
                layer.appendChild(img);
                gallery.appendChild(layer);
                layers.push(layer);
              } else {
                layer.hidden = false;
              }
              var imgEl = layer.querySelector('img');
              if (!imgEl) continue;
              var data = images[i];
              /* Shopify's `image_tag` Liquid filter auto-emits a srcset attribute
                 with default widths (165, 360, 533, 720, 940, 1066). The browser
                 prefers srcset over src for image resolution, so updating just
                 `src` leaves the old image visible. Clear srcset + sizes so the
                 new `src` is honored. (Reported 2026-07-12: BAS swatch swap
                 fired end-to-end - aria-pressed toggled, data-image-count updated,
                 img.src reassigned - but the visible image didn't change.) */
              imgEl.removeAttribute('srcset');
              imgEl.removeAttribute('sizes');
              imgEl.src = data.src;
              if (data.w) imgEl.width = data.w;
              if (data.h) imgEl.height = data.h;
              imgEl.alt = data.alt || '';
            }
            for (var j = newCount; j < layers.length; j++) {
              layers[j].hidden = true;
            }
            media.dataset.imageCount = String(newCount);
            media.dataset.idx = '0';
          }
        }
      }

      /* Toggle aria-pressed on the swatch row + flip card's data-sold-out
         based on the clicked swatch's data-variant-available. Runs for
         BOTH color-option products (no payload above) and variant-image
         products (payload present). Matches the Shop card pattern in
         component-product-card.js: the sold-out badge visibility follows
         the pressed swatch's variant availability. */
      var swatches = card.querySelectorAll('[data-action="bas-card-swatch"]');
      var clickedAvailable = null;
      for (var k = 0; k < swatches.length; k++) {
        var sw = swatches[k];
        var isClicked = sw.dataset.variantId === variantId;
        sw.setAttribute('aria-pressed', isClicked ? 'true' : 'false');
        if (isClicked) clickedAvailable = sw.dataset.variantAvailable;
      }
      if (clickedAvailable != null) {
        card.dataset.soldOut = clickedAvailable === 'false' ? 'true' : 'false';
      }
    }

    _onClick(e) {
      var actionEl = e.target.closest('[data-action]');
      var action = actionEl && actionEl.dataset.action;

      // chip click (rail OR modal)
      var chip = e.target.closest('[data-collection-handle]');
      if (chip && this.contains(chip)) {
        this._selectCollection(chip.dataset.collectionHandle, chip.hasAttribute('data-bas-all-chip'));
        return;
      }

      // arrow buttons
      var arrow = e.target.closest('[data-bas-chips-arrow]');
      if (arrow) {
        e.preventDefault();
        this._nudgeRail(parseInt(arrow.dataset.basChipsArrow, 10));
        return;
      }

      if (action === 'open-all-collections') {
        e.preventDefault();
        this._openAllModal();
        return;
      }
      if (action === 'close-all-collections') {
        e.preventDefault();
        this._closeAllModal();
        return;
      }
      if (action === 'clear-search') {
        e.preventDefault();
        this._setQuery('');
        var input = this.querySelector('[data-bas-search]');
        if (input) input.focus();
        return;
      }
      // Variant-image swatch on .bas-card (2026-07-12). closest('[data-action]')
      // matches the swatch button BEFORE the card's data-action="select-product"
      // root, so this branch fires first and preventDefault + stopPropagation
      // keep the card from opening the configurator on a swatch click.
      if (action === 'bas-card-swatch') {
        e.preventDefault();
        e.stopPropagation();
        var swCard = actionEl.closest('.bas-card');
        var swVariantId = actionEl.dataset.variantId;
        if (swCard && swVariantId) this._swapBasCardGallery(swCard, swVariantId);
        return;
      }
      // Quick-add: bypass the configurator step, add the pressed swatch's
      // variant directly at qty=1. closest('[data-action]') matches the
      // .bas-card__plus button BEFORE the ancestor .bas-card's
      // data-action="select-product", so this branch fires first and
      // stopPropagation prevents the configurator from also opening.
      if (action === 'quick-add') {
        e.preventDefault();
        e.stopPropagation();
        var qaCard = actionEl.closest('.bas-card');
        var qaProductId = qaCard && qaCard.dataset.productId;
        if (qaProductId) this._quickAdd(qaProductId, qaCard);
        return;
      }
      if (action === 'select-product') {
        e.preventDefault();
        var card = actionEl;
        var productId = card && card.dataset.productId;
        if (productId) this._selectProduct(productId);
        return;
      }
      // Hover-gallery arrows on .bas-card. closest('[data-action]') matches
      // the arrow span BEFORE the card's data-action="select-product" root,
      // so this branch fires first and prevents the card-select side effect.
      if (action === 'bas-card-nav') {
        e.preventDefault();
        e.stopPropagation();
        var basMedia = actionEl.closest('.bas-card__media');
        if (!basMedia) return;
        var basCount = parseInt(basMedia.dataset.imageCount, 10) || 1;
        if (basCount < 2) return;
        var basDir = parseInt(actionEl.dataset.basNavDir, 10);
        var basCurr = parseInt(basMedia.dataset.idx, 10) || 0;
        this._setBasCardIdx(basMedia, (basCurr + basDir + basCount) % basCount);
        return;
      }
      if (action === 'back-to-browse') {
        e.preventDefault();
        this._backToBrowse();
        return;
      }
      if (action === 'cfg-size') {
        e.preventDefault();
        var sizeBtn = e.target.closest('[data-cfg-size]');
        if (sizeBtn) this._setCfgSize(sizeBtn.dataset.cfgSize);
        return;
      }
      if (action === 'cfg-color') {
        e.preventDefault();
        var colorBtn = e.target.closest('[data-cfg-color]');
        if (colorBtn) this._setCfgColor(colorBtn.dataset.cfgColor);
        return;
      }
      if (action === 'cfg-qty-dec') {
        e.preventDefault();
        this._setCfgQty(Math.max(1, this._state.cfgQty - 1));
        return;
      }
      if (action === 'cfg-qty-inc') {
        e.preventDefault();
        if (this._state.cfgQty >= this._maxCfgQty()) return;
        this._setCfgQty(this._state.cfgQty + 1);
        return;
      }
      if (action === 'add-to-set') {
        e.preventDefault();
        this._addFromConfig();
        return;
      }
      if (action === 'checkout') {
        e.preventDefault();
        this._checkout();
        return;
      }
      if (action === 'set-item-dec') {
        e.preventDefault();
        var decKey = actionEl.dataset.itemKey;
        if (decKey) this._changeSetQty(decKey, -1);
        return;
      }
      if (action === 'set-item-inc') {
        e.preventDefault();
        var incKey = actionEl.dataset.itemKey;
        if (incKey) this._changeSetQty(incKey, 1);
        return;
      }
      if (action === 'set-item-remove') {
        e.preventDefault();
        var remKey = actionEl.dataset.itemKey;
        if (remKey) this._removeSetItem(remKey);
        return;
      }
      if (action === 'toggle-mobile-set') {
        e.preventDefault();
        this._toggleMobileDrawer();
        return;
      }
      if (action === 'close-mobile-set') {
        e.preventDefault();
        this._closeMobileDrawer();
        return;
      }
    }

    _onInput(e) {
      if (e.target.matches('[data-bas-search]')) {
        this._setQuery(e.target.value);
      } else if (e.target.matches('[data-bas-all-search]')) {
        this._filterModalChips(e.target.value);
      }
    }

    _onChange(e) {
      if (e.target.matches('[data-bas-sort]')) {
        this._setSort(e.target.value);
      }
    }

    _onKeydown(e) {
      // Keyboard activation for the .bas-card div[role="button"] (the card
      // root was changed from <button> to <div> so descendants can be <div>
      // for iOS Safari layout - see build-set-product-card.liquid). Buttons
      // natively activate on Enter/Space; div role=button needs this bridge.
      if (e.key === 'Enter' || e.key === ' ') {
        var card = e.target.closest('.bas-card[role="button"]');
        if (card && this.contains(card)) {
          e.preventDefault();
          card.click();
          return;
        }
      }
      if (e.key !== 'Escape') return;
      var modal = this.querySelector('[data-bas-all-collections-modal]');
      if (modal && !modal.hidden) {
        this._closeAllModal();
      }
    }

    // ---- configurator (product customize view) ---------------------------

    _findProduct(id) {
      var key = String(id);
      return (this._catalog || []).find(function (p) { return String(p.id) === key; }) || null;
    }

    _findVariant(product, size, color) {
      if (!product || !product.variants) return null;
      var positions = optionPositions(product);
      return product.variants.find(function (v) {
        var vSize = positions.size ? v['option' + positions.size] : null;
        var vColor = positions.color ? v['option' + positions.color] : null;
        return (positions.size === 0 || vSize === size) &&
               (positions.color === 0 || vColor === color);
      }) || null;
    }

    _selectProduct(productId) {
      var product = this._findProduct(productId);
      if (!product) return;
      var sizes = uniqSize(product);
      var colors = uniqColor(product).filter(function (c) { return c != null; });

      // Default size: the 700 ml variant when present (handoff JSX default),
      // else the first available size. Falls back to null for products with
      // no non-color option.
      var defaultSize = sizes.indexOf('700 ml') !== -1 ? '700 ml' : (sizes[0] || null);
      var defaultColor = colors[0] || null;

      this._state.selectedProductId = String(productId);
      this._state.cfgSize = defaultSize;
      this._state.cfgColor = defaultColor;
      this._state.cfgQty = 1;

      this._switchView('configurator');
      this._renderConfigurator();
      this._updateStep();
      this._scrollToConfigurator();
    }

    // Quick-add path: skip the configurator, add the currently-pressed
    // swatch's variant directly at qty=1. Size defaults to the same rule
    // _selectProduct uses (700 ml preferred, else first available) since
    // the browse card doesn't have a size picker. Reuses _addFromConfig
    // for the actual mutation so inventory clamping, tier-unlock detection,
    // set-changed event dispatch, and toast rendering all stay in one place.
    _quickAdd(productId, card) {
      var product = this._findProduct(productId);
      if (!product) return;
      var sizes = uniqSize(product);
      var defaultSize = sizes.indexOf('700 ml') !== -1 ? '700 ml' : (sizes[0] || null);

      // Determine color from the pressed swatch on the card. Products with
      // no Color option have no swatches (color = null); products with a
      // Color option always have at least one aria-pressed swatch (Liquid
      // seeds the first as pressed). Fall back to the first uniq color for
      // safety in case the swatch DOM was mutated externally.
      var color = null;
      var positions = optionPositions(product);
      if (positions.color) {
        var pressed = card && card.querySelector('[data-action="bas-card-swatch"][aria-pressed="true"]');
        if (pressed && pressed.dataset.variantId) {
          var pressedVariant = (product.variants || []).find(function (v) {
            return String(v.id) === String(pressed.dataset.variantId);
          });
          if (pressedVariant) color = pressedVariant['option' + positions.color];
        }
        if (color == null) {
          var colors = uniqColor(product).filter(function (c) { return c != null; });
          color = colors[0] || null;
        }
      }

      this._state.selectedProductId = String(productId);
      this._state.cfgSize = defaultSize;
      this._state.cfgColor = color;
      this._state.cfgQty = 1;
      this._addFromConfig();
    }

    _backToBrowse() {
      this._state.selectedProductId = null;
      this._switchView('browse');
      this._updateStep();
    }

    _setCfgSize(value) {
      this._state.cfgSize = value;
      this._renderConfigSizes();
      this._renderConfigEyebrow();
      this._renderConfigMedia();
      // Re-clamp cfgQty against the new variant's stock before rendering CTA -
      // a smaller-stock variant must not leave a stale qty above its max.
      this._setCfgQty(this._state.cfgQty);
    }

    _setCfgColor(value) {
      this._state.cfgColor = value;
      this._renderConfigColors();
      this._renderConfigEyebrow();
      this._renderConfigMedia();
      this._setCfgQty(this._state.cfgQty);
    }

    _setCfgQty(value) {
      var max = this._maxCfgQty();
      // Floor at 1 even when max is 0 so the displayed value never reads "0"
      // (it would be confusing under the +/- stepper). Add-to-set is blocked
      // separately in _addFromConfig / _renderConfigCta when max <= 0.
      var clamped = Math.max(1, Math.min(Math.floor(value) || 1, Math.max(1, max)));
      this._state.cfgQty = clamped;
      // Sync the shared <qty-stepper> CE's input value + data-max bounds
      // (aligned to PDP quantity control 2026-07-12; replaces the legacy
      // bas-cfg__qty-value span). Mutating the input's value directly is
      // enough - the CE's _syncDisabledState is called via the effective-max
      // update so +/- buttons reflect the new bounds.
      var qtyStepperEl = this.querySelector('qty-stepper');
      if (qtyStepperEl) {
        var input = qtyStepperEl.querySelector('.qty-stepper__input');
        if (input && String(clamped) !== input.value) input.value = String(clamped);
        var effectiveMax = isFinite(max) ? Math.max(1, max) : 99;
        qtyStepperEl.dataset.max = String(effectiveMax);
        if (typeof qtyStepperEl.max !== 'undefined') qtyStepperEl.max = effectiveMax;
        if (typeof qtyStepperEl._syncDisabledState === 'function') {
          qtyStepperEl._syncDisabledState();
        }
      }
      this._renderConfigCta();
    }

    /* Listen for qty-stepper:change events dispatched by the shared
       <qty-stepper> CE. The event bubbles to the build-set-configurator
       root; we filter by the input's `name` attribute so stray steppers
       elsewhere on the page (if any) can't cross-drive this state. */
    _onQtyStepperChange(e) {
      var stepper = e.target;
      if (!stepper || !stepper.querySelector) return;
      var input = stepper.querySelector('.qty-stepper__input');
      if (!input || input.name !== 'bas-cfg-qty') return;
      var val = e.detail && e.detail.value;
      if (typeof val === 'number' && val > 0) {
        this._setCfgQty(val);
      }
    }

    /**
     * Maximum qty the user can currently add for the active variant. Returns
     * Infinity when the variant is untracked or oversells are allowed by
     * the merchant - in that case the UI imposes no cap. When tracked + deny,
     * subtracts BOTH the qty already in the BAS local set AND the qty already
     * in the live Shopify cart for the same variant. Without the cart-side
     * subtraction, a user who already added the variant via PDP could try to
     * add up to stock again here, only to hit a 422 on commit.
     */
    _maxCfgQty() {
      var product = this._findProduct(this._state.selectedProductId);
      if (!product) return Infinity;
      var v = this._findVariant(product, this._state.cfgSize, this._state.cfgColor);
      if (!v) return Infinity;
      var managed = !!v.inventory_management && v.inventory_management !== '';
      var denyOversell = v.inventory_policy === 'deny';
      if (!managed || !denyOversell) return Infinity;
      var existingInSet = 0;
      for (var i = 0; i < this._state.set.length; i++) {
        if (this._state.set[i].variantId === v.id) existingInSet += this._state.set[i].qty;
      }
      var existingInCart = 0;
      var cart = window.cartStore && typeof window.cartStore.getCached === 'function'
        ? window.cartStore.getCached()
        : null;
      if (cart && Array.isArray(cart.items)) {
        for (var j = 0; j < cart.items.length; j++) {
          if (cart.items[j].variant_id === v.id) existingInCart += (cart.items[j].quantity || 0);
        }
      }
      return Math.max(0, (v.inventory_quantity || 0) - existingInSet - existingInCart);
    }

    _switchView(view) {
      this.querySelectorAll('[data-bas-view]').forEach(function (el) {
        el.hidden = el.dataset.basView !== view;
      });
    }

    _scrollToConfigurator() {
      // Target the section wrapper (which includes the step bar + layout),
      // not the configurator view itself. The step bar gives the user
      // crucial "you are here" context (Customize step active); scrolling
      // straight to the configurator skips past it. The 84px offset lands
      // the step bar just below the sticky header (~73px header + a bit
      // of breathing room).
      var anchor = this.querySelector('.bas-section') || this.querySelector('[data-bas-view="configurator"]');
      if (!anchor) return;
      requestAnimationFrame(function () {
        var y = anchor.getBoundingClientRect().top + window.scrollY - 84;
        window.scrollTo({ top: Math.max(0, y), behavior: 'auto' });
      });
    }

    _renderConfigurator() {
      var product = this._findProduct(this._state.selectedProductId);
      if (!product) return;

      // Title
      var titleEl = this.querySelector('[data-bas-cfg-title]');
      if (titleEl) titleEl.textContent = product.title || '';

      // Rating (only if metafield-driven rating present on the card data)
      var card = this.querySelector('.bas-card[data-product-id="' + CSS.escape(this._state.selectedProductId) + '"]');
      var rating = card && parseFloat(card.dataset.productRating || '0');
      var reviews = card && parseInt(card.dataset.productReviews || '0', 10);
      var ratingEl = this.querySelector('[data-bas-cfg-rating]');
      var ratingText = this.querySelector('[data-bas-cfg-rating-text]');
      if (ratingEl) {
        if (rating > 0) {
          ratingEl.hidden = false;
          if (ratingText) {
            var tmpl = reviews === 1
              ? copy('cfg_reviews_one', '__COUNT__ review')
              : copy('cfg_reviews_other', '__COUNT__ reviews');
            ratingText.textContent = rating + ' · ' + tmpl.replace('__COUNT__', reviews.toLocaleString());
          }
        } else {
          ratingEl.hidden = true;
        }
      }

      // Badge
      this._renderConfigBadge(product);

      // Sizes
      this._renderConfigSizes();

      // Colors
      this._renderConfigColors();

      // Eyebrow + media + CTA
      this._renderConfigEyebrow();
      this._renderConfigMedia();
      this._renderConfigCta();

      // Qty value: route through _setCfgQty so the shared <qty-stepper> CE's
      // input + effective-max bounds get synced (aligned to PDP 2026-07-12).
      this._setCfgQty(this._state.cfgQty);
    }

    _renderConfigBadge(product) {
      var badgeEl = this.querySelector('[data-bas-cfg-badge]');
      if (!badgeEl) return;
      var b = badgeFor(product);
      if (!b) {
        badgeEl.hidden = true;
        badgeEl.textContent = '';
        badgeEl.removeAttribute('data-badge-tone');
        return;
      }
      badgeEl.hidden = false;
      badgeEl.textContent = b.label;
      badgeEl.setAttribute('data-badge-tone', b.tone);
    }

    _renderConfigSizes() {
      var grid = this.querySelector('[data-bas-cfg-size-grid]');
      var section = this.querySelector('[data-bas-cfg-size-section]');
      if (!grid || !section) return;
      var product = this._findProduct(this._state.selectedProductId);
      if (!product) return;
      var sizes = uniqSize(product);

      // Hide section entirely if only one (or zero) size value. Zero
      // happens when the product has no non-color option (e.g. its only
      // option is Colorway). Clear the grid so stale tiles from a prior
      // product don't linger in the hidden DOM.
      if (sizes.length <= 1) {
        section.hidden = true;
        grid.innerHTML = '';
        return;
      }
      section.hidden = false;

      // Sync heading + aria-label to the product's actual size-option name
      // (Size / Type / Length / etc.) so the label matches the CMS.
      var sizePos = optionPositions(product).size;
      var sizeName = sizePos ? (product.options[sizePos - 1] || '') : '';
      if (sizeName) {
        var headingEl = this.querySelector('[data-bas-cfg-size-heading]');
        if (headingEl) headingEl.textContent = sizeName;
        grid.setAttribute('aria-label', sizeName);
      }

      // Inline label suffix: "SIZE - 700 ml" (aligned to PDP 2026-07-12).
      var sizeLabelEl = this.querySelector('[data-bas-cfg-size-label]');
      if (sizeLabelEl) {
        sizeLabelEl.textContent = this._state.cfgSize ? (' - ' + this._state.cfgSize) : '';
      }

      var current = this._state.cfgSize;
      var color = this._state.cfgColor;
      var self = this;

      /* Variant-image thumbnails ALSO applies to the size axis (2026-07-12)
         when the option is really a non-color, non-size axis (e.g. Toy Trio's
         "Toy Trio" option name that lists Sheriff Blue / Rodeo Red / Galaxy
         Green). optionPositions() can't robustly detect "colorway"-style
         axes by name, so the sensible rule is: if every value has a distinct
         variant.image and no admin swatch metadata, render thumbnails.
         Real size axes (ml / oz / S-M-L) rarely have distinct variant.image
         per size, so the thumbnail branch stays inactive for them. */
      var variants = (product && product.variants) || [];
      var sizeKey = 'option' + sizePos;
      var sizeEveryHasImage = sizes.every(function (s) {
        var rep = variants.find(function (v) { return v && v[sizeKey] === s; });
        return !!(rep && rep.image);
      });
      var useThumbs = sizeEveryHasImage;
      grid.classList.toggle('bas-cfg__size-grid--thumbs', useThumbs);

      var html = sizes.map(function (s) {
        var v = self._findVariant(product, s, color);
        var sel = s === current;
        if (useThumbs) {
          var rep = variants.find(function (vv) { return vv && vv[sizeKey] === s; });
          var imgSrc = rep && rep.image ? rep.image : '';
          return (
            '<button type="button" class="bas-cfg__color-thumb' + (sel ? ' is-selected' : '') + '"' +
            ' data-action="cfg-size" data-cfg-size="' + escapeAttr(s) + '"' +
            ' role="radio" aria-checked="' + (sel ? 'true' : 'false') + '"' +
            ' aria-label="' + escapeAttr(s) + '"' +
            ' title="' + escapeAttr(s) + '">' +
            '<img src="' + escapeAttr(imgSrc) + '" alt="" width="40" height="40" loading="lazy" decoding="async">' +
            '</button>'
          );
        }
        /* Client request 2026-07-12: tile shows the option value only.
           Per-tile descriptor ("Snack · square") and price ("₱1") were
           removed - descriptor lives in the Care Guide, per-tile price is
           redundant with the variant price rendered under the title. */
        return (
          '<button type="button" class="bas-cfg__size-tile' + (sel ? ' is-selected' : '') + '"' +
          ' data-action="cfg-size" data-cfg-size="' + escapeAttr(s) + '"' +
          ' role="radio" aria-checked="' + (sel ? 'true' : 'false') + '">' +
            '<span class="bas-cfg__size-ml">' + escapeText(s) + '</span>' +
          '</button>'
        );
      }).join('');
      grid.innerHTML = html;
    }

    _renderConfigColors() {
      var grid = this.querySelector('[data-bas-cfg-color-grid]');
      var section = grid ? grid.closest('.bas-cfg__color-section') : null;
      if (!grid) return;
      var product = this._findProduct(this._state.selectedProductId);
      if (!product) return;
      var colors = uniqColor(product).filter(function (c) { return c != null; });

      // Hide the whole color section for products with no color-like
      // option (e.g. size-only products). Symmetric with the size hide
      // above so the panel doesn't render an empty "COLOR" heading.
      if (section) section.hidden = colors.length === 0;
      if (colors.length === 0) { grid.innerHTML = ''; return; }

      var current = this._state.cfgColor;

      // Sync heading + aria-label to the product's actual color-option name
      // (Color / Colour / Colorway / etc.) so the label matches the CMS.
      var colorPos = optionPositions(product).color;
      var colorName = colorPos ? (product.options[colorPos - 1] || '') : '';
      if (colorName) {
        var headingEl = this.querySelector('[data-bas-cfg-color-heading]');
        if (headingEl) headingEl.textContent = colorName;
        grid.setAttribute('aria-label', colorName);
      }

      // Inline label suffix: "COLOR - Sheriff Blue" (aligned to PDP 2026-07-12,
      // matches sections/product-buy-box.liquid legend format).
      var labelEl = this.querySelector('[data-bas-cfg-color-label]');
      if (labelEl) labelEl.textContent = current ? (' - ' + current) : '';

      var swatches = (product && product.swatches) || {};
      var variants = (product && product.variants) || [];
      // Same gate as the PDP (sections/product-buy-box.liquid 2026-07-12):
      // if no color has admin swatch metadata AND every color's representative
      // variant has an image → render variant-image thumbnails instead of
      // text tiles. If any color has admin metadata → color dots. Else text
      // tiles as the fallback.
      var anySwatchColor = colors.some(function (c) {
        return !!(swatches[c] && swatches[c] !== '');
      });
      var everyHasImage = colors.every(function (c) {
        var rep = variants.find(function (v) {
          return v && (v.option1 === c || v.option2 === c);
        });
        return !!(rep && rep.image);
      });
      var useThumbs = !anySwatchColor && everyHasImage;
      var hasAnyTile = colors.some(function (c) {
        return !(swatches[c] && swatches[c] !== '');
      });
      grid.classList.toggle('bas-cfg__color-grid--thumbs', useThumbs);
      grid.classList.toggle('bas-cfg__color-grid--tiles', hasAnyTile && !useThumbs);
      var html = colors.map(function (c) {
        var sel = c === current;
        if (useThumbs) {
          var rep = variants.find(function (v) {
            return v && (v.option1 === c || v.option2 === c);
          });
          var imgSrc = rep && rep.image ? rep.image : '';
          return (
            '<button type="button" class="bas-cfg__color-thumb' + (sel ? ' is-selected' : '') + '"' +
            ' data-action="cfg-color" data-cfg-color="' + escapeAttr(c) + '"' +
            ' role="radio" aria-checked="' + (sel ? 'true' : 'false') + '"' +
            ' aria-label="' + escapeAttr(c) + '"' +
            ' title="' + escapeAttr(c) + '">' +
            '<img src="' + escapeAttr(imgSrc) + '" alt="" width="40" height="40" loading="lazy" decoding="async">' +
            '</button>'
          );
        }
        var adminSwatch = swatches[c];
        if (adminSwatch && adminSwatch !== '') {
          return (
            '<button type="button" class="bas-cfg__color' + (sel ? ' is-selected' : '') + '"' +
            ' data-action="cfg-color" data-cfg-color="' + escapeAttr(c) + '"' +
            ' role="radio" aria-checked="' + (sel ? 'true' : 'false') + '"' +
            ' aria-label="' + escapeAttr(c) + '"' +
            ' title="' + escapeAttr(c) + '"' +
            ' style="--bas-color-swatch: ' + adminSwatch + ';"></button>'
          );
        }
        return (
          '<button type="button" class="bas-cfg__color-tile' + (sel ? ' is-selected' : '') + '"' +
          ' data-action="cfg-color" data-cfg-color="' + escapeAttr(c) + '"' +
          ' role="radio" aria-checked="' + (sel ? 'true' : 'false') + '"' +
          ' aria-label="' + escapeAttr(c) + '"' +
          ' title="' + escapeAttr(c) + '">' +
          escapeText(c) +
          '</button>'
        );
      }).join('');
      grid.innerHTML = html;
    }

    _renderConfigEyebrow() {
      var eb = this.querySelector('[data-bas-cfg-eyebrow]');
      if (!eb) return;
      var product = this._findProduct(this._state.selectedProductId);
      if (!product) return;
      var color = this._state.cfgColor || '';
      var size = this._state.cfgSize || '';
      var sizes = uniqSize(product);
      var hasSize = sizes.length > 0 && size;
      // Format: "<color> · <size>" or just "<color>" when single-size product.
      eb.textContent = hasSize && sizes.length > 1
        ? color + ' · ' + size
        : (color || size || '');
    }

    _renderConfigMedia() {
      var product = this._findProduct(this._state.selectedProductId);
      if (!product) return;
      var v = this._findVariant(product, this._state.cfgSize, this._state.cfgColor);
      var imgEl = this.querySelector('[data-bas-cfg-img]');
      if (imgEl) {
        var src = (v && v.image) || product.image || '';
        if (src) imgEl.setAttribute('src', src);
        imgEl.setAttribute('alt', product.title || '');
      }
    }

    _renderConfigCta() {
      var product = this._findProduct(this._state.selectedProductId);
      if (!product) return;
      var v = this._findVariant(product, this._state.cfgSize, this._state.cfgColor);
      var price = v ? v.price : product.from_price || 0;
      var qty = this._state.cfgQty;
      var lineTotal = price * qty;
      var max = this._maxCfgQty();

      /* Variant price under the title (added 2026-07-12 for PDP-parity in the
         configurator header). Shows the variant's OWN price - NOT qty-adjusted -
         so the header reads like the standard PDP: title / price / picker. The
         qty-adjusted line total lives on the ADD TO MY SET CTA. */
      var variantPriceEl = this.querySelector('[data-bas-cfg-variant-price]');
      if (variantPriceEl) variantPriceEl.textContent = v ? peso(v.price) : peso(product.from_price || 0);
      var variantCompareEl = this.querySelector('[data-bas-cfg-variant-compare]');
      if (variantCompareEl) {
        var vc = v && v.compare_at_price ? v.compare_at_price : 0;
        var vp = v ? v.price : 0;
        if (vc > vp) {
          variantCompareEl.textContent = peso(vc);
          variantCompareEl.hidden = false;
        } else {
          variantCompareEl.textContent = '';
          variantCompareEl.hidden = true;
        }
      }

      var totalEl = this.querySelector('[data-bas-cfg-total]');
      if (totalEl) totalEl.textContent = peso(lineTotal);

      // Compare-at strikethrough - shown only when the current variant is
      // marked-down (compare_at_price strictly greater than price). The
      // strikethrough shows the full line total at compare_at so the
      // savings scale with qty just like the actual total.
      var compareEl = this.querySelector('[data-bas-cfg-compare]');
      if (compareEl) {
        var vCap = v && v.compare_at_price ? v.compare_at_price : 0;
        var vPrice = v ? v.price : 0;
        if (vCap > vPrice) {
          compareEl.textContent = peso(vCap * qty);
          compareEl.hidden = false;
        } else {
          compareEl.textContent = '';
          compareEl.hidden = true;
        }
      }

      var ctaLabel = this.querySelector('[data-bas-cfg-add-label]');
      var ctaBtn = this.querySelector('[data-action="add-to-set"]');
      var incBtn = this.querySelector('[data-action="cfg-qty-inc"]');
      var hintEl = this.querySelector('[data-bas-cfg-stock-hint]');

      if (ctaLabel) {
        var baseLabel;
        if (max <= 0) {
          baseLabel = copy('cfg_out_of_stock', 'OUT OF STOCK');
          ctaLabel.textContent = baseLabel;
        } else if (qty > 1) {
          baseLabel = copy('cfg_add_n_to_set', 'ADD __COUNT__ TO MY SET').replace('__COUNT__', String(qty));
          ctaLabel.textContent = baseLabel + ' · ' + peso(lineTotal);
        } else {
          baseLabel = copy('add_to_set', 'ADD TO MY SET');
          ctaLabel.textContent = baseLabel + ' · ' + peso(lineTotal);
        }
      }
      if (ctaBtn) {
        if (max <= 0) ctaBtn.setAttribute('disabled', '');
        else ctaBtn.removeAttribute('disabled');
      }
      if (incBtn) {
        if (max !== Infinity && qty >= max) incBtn.setAttribute('disabled', '');
        else incBtn.removeAttribute('disabled');
      }
      if (hintEl) {
        var hintText = '';
        if (max === 0) {
          hintText = copy('cfg_stock_none', 'Out of stock for your set');
        } else if (max !== Infinity && max <= 10) {
          var tmpl = copy('cfg_stock_low', 'Only __COUNT__ left');
          hintText = tmpl.replace('__COUNT__', String(max));
        }
        hintEl.textContent = hintText;
        hintEl.hidden = hintText === '';
      }
    }

    _addFromConfig() {
      var product = this._findProduct(this._state.selectedProductId);
      if (!product) return;
      var v = this._findVariant(product, this._state.cfgSize, this._state.cfgColor);
      if (!v) {
        console.warn('[build-set] no variant for', this._state.cfgSize, this._state.cfgColor);
        return;
      }
      var self = this;

      // Inventory guard. When the variant is tracked by Shopify and the policy
      // blocks oversells, cap the add so we never commit a set that would 422
      // on cart/add. We do NOT account for the current Shopify cart contents -
      // those are not visible to the BAS local state - so the eventual commit
      // can still fail if other tabs / lines push us past stock. The toast
      // here is best-effort prevention, not a hard guarantee.
      var managed = !!v.inventory_management && v.inventory_management !== '';
      var denyOversell = v.inventory_policy === 'deny';
      var requestedQty = this._state.cfgQty;
      var clampedQty = requestedQty;
      var availableNow = null;
      if (managed && denyOversell) {
        var existingForVariantInSet = 0;
        for (var i = 0; i < this._state.set.length; i++) {
          if (this._state.set[i].variantId === v.id) {
            existingForVariantInSet += this._state.set[i].qty;
          }
        }
        availableNow = Math.max(0, (v.inventory_quantity || 0) - existingForVariantInSet);
        if (requestedQty > availableNow) {
          clampedQty = availableNow;
        }
      }
      if (managed && denyOversell && clampedQty <= 0) {
        this._flashToast('inventory_none', { title: product.title });
        this._backToBrowse();
        return;
      }

      // Snapshot box count BEFORE the mutation to detect tier-up after.
      var beforeBoxes = this._state.set.reduce(function (n, it) {
        return n + (self._isBox(it) ? it.qty : 0);
      }, 0);

      var key = product.id + '|' + this._state.cfgSize + '|' + this._state.cfgColor;
      var existingIdx = this._state.set.findIndex(function (it) { return it.key === key; });
      if (existingIdx >= 0) {
        this._state.set[existingIdx] = Object.assign({}, this._state.set[existingIdx],
          { qty: this._state.set[existingIdx].qty + clampedQty });
      } else {
        this._state.set.push({
          key: key,
          productId: product.id,
          variantId: v.id,
          size: this._state.cfgSize,
          color: this._state.cfgColor,
          qty: clampedQty,
          price: v.price,
          title: product.title,
          image: v.image || product.image,
          type: product.type
        });
      }
      this._state.lastKey = key;

      // Capture before/after box counts for tier-unlock detection AFTER
      // the splice/push has occurred. Box count is derived from current
      // state every call.
      var afterBoxes = this._state.set.reduce(function (n, it) {
        return n + (self._isBox(it) ? it.qty : 0);
      }, 0);
      // Note: beforeBoxes was snapshotted above before the state mutation.

      this._renderSummary();
      this._updateStep();

      var unlocked = this._maybeCelebrate(beforeBoxes, afterBoxes);
      // When the requested qty was clamped by stock, the validation toast
      // takes priority over the celebration / added toast - the user needs
      // to know that fewer items landed in the set than they typed.
      if (clampedQty < requestedQty) {
        this._flashToast('inventory_clamped', { title: product.title, added: clampedQty, requested: requestedQty });
      } else {
        this._flashToast(unlocked ? 'tier_unlocked' : 'added', { title: product.title, pct: this._tierPctFor(afterBoxes) });
      }

      // Dispatch so external listeners (analytics, tests) can observe.
      this.dispatchEvent(new CustomEvent('build-set:set-changed', {
        bubbles: true,
        detail: { set: this._state.set, lastKey: key }
      }));

      this._backToBrowse();
    }

    // ---- set + summary + meter -------------------------------------------

    _changeSetQty(key, delta) {
      var idx = this._state.set.findIndex(function (it) { return it.key === key; });
      if (idx < 0) return;
      var next = this._state.set[idx].qty + delta;
      if (next <= 0) {
        this._state.set.splice(idx, 1);
      } else {
        this._state.set[idx] = Object.assign({}, this._state.set[idx], { qty: next });
      }
      this._renderSummary();
      this._updateStep();
    }

    _removeSetItem(key) {
      var idx = this._state.set.findIndex(function (it) { return it.key === key; });
      if (idx < 0) return;
      this._state.set.splice(idx, 1);
      this._renderSummary();
      this._updateStep();
    }

    _isBox(item) {
      // BUSINESS_RULES.md: only box-type products count toward the bundle
      // tier. We classify by product type comparison against the configured
      // box type. For now, any product type matching /lunch box/i counts.
      if (!item || !item.type) return true; // default to box-eligible
      return /lunch box/i.test(item.type);
    }

    _renderSummary() {
      var set = this._state.set;
      var count = set.reduce(function (n, it) { return n + it.qty; }, 0);

      // Box-only count drives the tier discount; subtotal is the full set.
      var self = this;
      var boxCount = set.reduce(function (n, it) {
        return n + (self._isBox(it) ? it.qty : 0);
      }, 0);
      var subtotal = set.reduce(function (n, it) { return n + (it.price * it.qty); }, 0);
      var boxSubtotal = set.reduce(function (n, it) {
        return n + (self._isBox(it) ? it.price * it.qty : 0);
      }, 0);
      var pct = this._tierPctFor(boxCount);
      var saved = Math.round(boxSubtotal * pct / 100);
      var total = subtotal - saved;

      // Headings: "(N)" suffix everywhere.
      var countStr = '(' + count + ')';
      this.querySelectorAll('[data-bas-summary-count]').forEach(function (el) {
        el.textContent = countStr;
      });

      // Sidebar header pill (desktop). Hidden until discount > 0.
      this.querySelectorAll('[data-bas-summary-pill]').forEach(function (el) {
        if (pct > 0) {
          el.hidden = false;
          el.textContent = pct + '% off';
        } else {
          el.hidden = true;
          el.textContent = '';
        }
      });

      // Items list - rebuild from current state in every summary-body root.
      this._renderSummaryItems();

      // Subtotal label (e.g. "Subtotal · 3 boxes")
      var subCountStr = '';
      if (boxCount > 0) {
        var tmpl = boxCount === 1
          ? copy('subtotal_with_count_one', ' · 1 box')
          : copy('subtotal_with_count_other', ' · __COUNT__ boxes');
        subCountStr = tmpl.replace('__COUNT__', String(boxCount));
      }
      this.querySelectorAll('[data-bas-summary-sub-count]').forEach(function (el) {
        el.textContent = subCountStr;
      });

      this.querySelectorAll('[data-bas-summary-subtotal]').forEach(function (el) {
        el.textContent = peso(subtotal);
        el.style.textDecoration = saved > 0 ? 'line-through' : 'none';
      });
      this.querySelectorAll('[data-bas-summary-savings-row]').forEach(function (el) {
        el.hidden = !(saved > 0);
      });
      this.querySelectorAll('[data-bas-summary-pct-label]').forEach(function (el) {
        el.textContent = String(pct);
      });
      this.querySelectorAll('[data-bas-summary-saved]').forEach(function (el) {
        el.textContent = peso(saved);
      });
      this.querySelectorAll('[data-bas-summary-total]').forEach(function (el) {
        el.textContent = peso(total);
      });

      // CTA: disabled when set is empty.
      var ctaTemplate = count > 0
        ? copy('add_to_bag_cta_with_total', 'Add set to bag · __TOTAL__').replace('__TOTAL__', peso(total))
        : copy('empty_cta', 'Add a box to start');
      this.querySelectorAll('[data-action="checkout"]').forEach(function (el) {
        el.disabled = count === 0;
      });
      this.querySelectorAll('[data-bas-summary-cta-label]').forEach(function (el) {
        el.textContent = ctaTemplate;
      });

      this._renderMeter(boxCount);
      this._renderMobileBar(count, boxCount, pct, total);
    }

    /**
     * Find the icons.svg sprite URL by reading any existing <use href="...#icon-x">
     * already rendered on the page (the snippet `icon.liquid` emits these for
     * every Liquid {% render 'icon' %} callsite). Returns the URL minus the
     * fragment so callers can append `#icon-minus` / `#icon-plus`. Falls back
     * to the same-origin relative path - that doesn't work on the proxied dev
     * preview but is safe for production.
     *
     * Sprite refs are used here (instead of inline <svg><path>) because
     * iOS Safari fails to render small inline-SVG strokes at sub-pixel widths
     * (the sprite's <symbol> is rasterized once at sprite load and reused -
     * iOS renders it cleanly regardless of consumer SVG size). LESSONS.md
     * "iOS Safari renders inline-SVG strokes invisibly when stroke-width is
     * non-integer at small render sizes" has the full diagnosis.
     */
    _iconSpriteHref() {
      if (this._cachedSpriteHref !== undefined) return this._cachedSpriteHref;
      var use = document.querySelector('use[href*="icons.svg"]');
      if (use) {
        var ref = use.getAttribute('href') || '';
        var hashIdx = ref.indexOf('#');
        if (hashIdx > 0) {
          this._cachedSpriteHref = ref.slice(0, hashIdx);
          return this._cachedSpriteHref;
        }
      }
      this._cachedSpriteHref = '/cdn/shop/t/7/assets/icons.svg';
      return this._cachedSpriteHref;
    }

    _renderSummaryItems() {
      var set = this._state.set;
      var roots = this.querySelectorAll('[data-bas-summary-items]');
      var self = this;
      var spriteHref = this._iconSpriteHref();
      roots.forEach(function (root) {
        // Strip prior dynamic rows; preserve the empty-state placeholder.
        Array.prototype.slice.call(root.querySelectorAll('[data-bas-summary-item]')).forEach(function (n) {
          n.remove();
        });
        var emptyEl = root.querySelector('[data-bas-summary-empty]');
        if (set.length === 0) {
          if (emptyEl) emptyEl.hidden = false;
          return;
        }
        if (emptyEl) emptyEl.hidden = true;
        set.forEach(function (it) {
          var img = it.image || '';
          var meta = (it.color || '') + (it.size ? ' · ' + it.size : '') + ' · ' + peso(it.price);
          var keyAttr = escapeAttr(it.key);
          var row = document.createElement('div');
          row.dataset.basSummaryItem = '1';
          row.className = 'bas-summary-item';
          row.innerHTML = (
            '<span class="bas-summary-item__media">' +
              (img ? '<img src="' + escapeAttr(img) + '" alt="">' : '<span class="bas-summary-item__media-placeholder"></span>') +
            '</span>' +
            '<span class="bas-summary-item__body">' +
              '<span class="bas-summary-item__title">' + escapeText(it.title || '') + '</span>' +
              '<span class="bas-summary-item__meta">' + escapeText(meta) + '</span>' +
              '<span class="bas-summary-item__qty">' +
                '<button type="button" class="bas-summary-item__qty-btn" data-action="set-item-dec" data-item-key="' + keyAttr + '" aria-label="' + escapeAttr(copy('decrease_qty', 'Decrease quantity')) + '">' +
                  '<svg class="icon icon--minus" width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><use href="' + spriteHref + '#icon-minus"></use></svg>' +
                '</button>' +
                '<span class="bas-summary-item__qty-value">' + escapeText(String(it.qty)) + '</span>' +
                '<button type="button" class="bas-summary-item__qty-btn" data-action="set-item-inc" data-item-key="' + keyAttr + '" aria-label="' + escapeAttr(copy('increase_qty', 'Increase quantity')) + '">' +
                  '<svg class="icon icon--plus" width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><use href="' + spriteHref + '#icon-plus"></use></svg>' +
                '</button>' +
              '</span>' +
            '</span>' +
            '<button type="button" class="bas-summary-item__remove" data-action="set-item-remove" data-item-key="' + keyAttr + '" aria-label="' + escapeAttr(copy('remove_item', 'Remove')) + '">' +
              '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M10 11v6M14 11v6M5 6l1 14a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-14"/></svg>' +
            '</button>'
          );
          root.appendChild(row);
        });
      });
    }

    // ---- discount meter --------------------------------------------------

    _tierPctFor(boxCount) {
      var tiers = (this._config && this._config.tiers) || [];
      var pct = 0;
      for (var i = 0; i < tiers.length; i++) {
        if (boxCount >= tiers[i].qty) pct = tiers[i].pct;
      }
      return pct;
    }

    _nextTier(boxCount) {
      var tiers = (this._config && this._config.tiers) || [];
      for (var i = 0; i < tiers.length; i++) {
        if (boxCount < tiers[i].qty) return tiers[i];
      }
      return null;
    }

    _meterFillPct(boxCount) {
      // Mirrors BuildSetPage.jsx:587-596 - even-pos nodes per tier; fill
      // interpolates linearly between consecutive tier positions.
      var tiers = (this._config && this._config.tiers) || [];
      if (tiers.length === 0) return 0;
      var nodes = [{ boxes: 0, pos: 0 }];
      tiers.forEach(function (t, k) {
        nodes.push({ boxes: t.qty, pos: (k + 1) / tiers.length * 100 });
      });
      var fill = 100;
      for (var i = 0; i < nodes.length - 1; i++) {
        var a = nodes[i], b = nodes[i + 1];
        if (boxCount < b.boxes) {
          var frac = (boxCount - a.boxes) / (b.boxes - a.boxes);
          fill = a.pos + frac * (b.pos - a.pos);
          return Math.max(0, Math.min(100, fill));
        }
      }
      return 100;
    }

    _renderMeter(boxCount) {
      var pct = this._tierPctFor(boxCount);
      var fill = this._meterFillPct(boxCount);
      var next = this._nextTier(boxCount);
      var tiers = (this._config && this._config.tiers) || [];

      // Percent label + fill width on each meter instance.
      this.querySelectorAll('[data-bas-meter-pct]').forEach(function (el) {
        el.textContent = pct + '%';
      });
      this.querySelectorAll('[data-bas-meter-fill]').forEach(function (el) {
        el.style.width = fill + '%';
      });
      this.querySelectorAll('[data-bas-meter-next]').forEach(function (el) {
        var msg;
        if (boxCount === 0) {
          msg = copy('meter_start', 'Add 2 boxes to unlock 5% off');
        } else if (next) {
          var n = next.qty - boxCount;
          var tmpl = n === 1
            ? copy('meter_next_one', 'Add 1 more box to unlock __PCT__%')
            : copy('meter_next_other', 'Add __N__ more boxes to unlock __PCT__%');
          msg = tmpl.replace('__N__', String(n)).replace('__PCT__', String(next.pct));
        } else {
          msg = copy('meter_max', 'Max savings - 30% off!');
        }
        el.textContent = msg;
        el.classList.toggle('is-maxed', !next);
      });

      // Marker reached state.
      this.querySelectorAll('[data-bas-meter-marker]').forEach(function (el) {
        var k = parseInt(el.dataset.tierIndex, 10);
        var tier = tiers[k - 1];
        var reached = tier && boxCount >= tier.qty;
        el.classList.toggle('is-reached', !!reached);
      });
      this.querySelectorAll('[data-bas-meter-label]').forEach(function (el) {
        var k = parseInt(el.dataset.tierIndex, 10);
        var tier = tiers[k - 1];
        var reached = tier && boxCount >= tier.qty;
        el.classList.toggle('is-reached', !!reached);
      });
    }

    // ---- mobile bar / drawer ---------------------------------------------

    _renderMobileBar(count, boxCount, pct, total) {
      var bar = this.querySelector('[data-bas-mobile-summary]');
      if (!bar) return;
      var countLine = bar.querySelector('[data-bas-mobile-count]');
      var priceLine = bar.querySelector('[data-bas-mobile-price]');
      var viewPill = bar.querySelector('[data-bas-mobile-view-pill]');

      if (count === 0) {
        if (countLine) countLine.textContent = copy('empty_title', 'Your set is empty');
        if (priceLine) priceLine.textContent = copy('empty_cta', 'Add a box to start');
        if (viewPill) viewPill.hidden = true;
        return;
      }
      var boxLabel = boxCount === 1
        ? copy('mobile_count_one', '1 box')
        : copy('mobile_count_other', '__COUNT__ boxes');
      boxLabel = boxLabel.replace('__COUNT__', String(boxCount));
      var line = boxLabel;
      if (pct > 0) {
        line = copy('mobile_count_with_pct', '__COUNT__ · __PCT__% off')
          .replace('__COUNT__', boxLabel)
          .replace('__PCT__', String(pct));
      }
      if (countLine) countLine.textContent = line;
      if (priceLine) priceLine.textContent = peso(total);
      if (viewPill) viewPill.hidden = false;
    }

    _toggleMobileDrawer() {
      var bar = this.querySelector('[data-bas-mobile-summary]');
      if (!bar) return;
      var drawer = bar.querySelector('[data-bas-mobile-drawer]');
      var backdrop = bar.querySelector('[data-bas-mobile-backdrop]');
      var btn = bar.querySelector('[data-action="toggle-mobile-set"]');
      var open = drawer && drawer.hidden;
      // Only allow open when set has at least one item.
      if (open && this._state.set.length === 0) return;
      if (drawer) drawer.hidden = !open;
      if (backdrop) backdrop.hidden = !open;
      if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.documentElement.style.overflow = open ? 'hidden' : '';
    }

    _closeMobileDrawer() {
      var bar = this.querySelector('[data-bas-mobile-summary]');
      if (!bar) return;
      var drawer = bar.querySelector('[data-bas-mobile-drawer]');
      var backdrop = bar.querySelector('[data-bas-mobile-backdrop]');
      var btn = bar.querySelector('[data-action="toggle-mobile-set"]');
      if (drawer) drawer.hidden = true;
      if (backdrop) backdrop.hidden = true;
      if (btn) btn.setAttribute('aria-expanded', 'false');
      document.documentElement.style.overflow = '';
    }

    // ---- celebration + toast (Batch E) -----------------------------------

    _maybeCelebrate(beforeBoxes, afterBoxes) {
      var prev = this._tierPctFor(beforeBoxes);
      var next = this._tierPctFor(afterBoxes);
      if (next <= prev) return false;
      this._pulseMeterPct();
      if (this.dataset.showConfetti !== 'false') this._fireConfetti();
      return true;
    }

    _pulseMeterPct() {
      // 0.5s basPop on each percent number. Re-add the class so re-fires
      // get a fresh animation cycle even if the prior one is still running.
      this.querySelectorAll('[data-bas-meter-pct]').forEach(function (el) {
        el.classList.remove('is-pulsing');
        // Force reflow so the re-added class restarts the animation.
        void el.offsetWidth;
        el.classList.add('is-pulsing');
        setTimeout(function () { el.classList.remove('is-pulsing'); }, 600);
      });
    }

    _fireConfetti() {
      // Respect reduced-motion preference - confetti is decoration only.
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      var colors = ['var(--sun-400)', 'var(--tomato-400)', 'var(--magenta-400)', 'var(--blue-400)', 'var(--blue-300)'];
      this.querySelectorAll('[data-bas-confetti]').forEach(function (host) {
        // Clear any in-flight particles (overlapping celebrations).
        host.innerHTML = '';
        var N = 14;
        for (var i = 0; i < N; i++) {
          var ang = (Math.PI * 2) * (i / N) + (Math.random() * 0.5);
          var dist = 60 + Math.random() * 70;
          var bit = document.createElement('span');
          bit.className = 'bas-confetti__bit';
          var sz = 6 + Math.random() * 6;
          bit.style.setProperty('--tx', (Math.cos(ang) * dist).toFixed(1) + 'px');
          bit.style.setProperty('--ty', (Math.sin(ang) * dist - 20).toFixed(1) + 'px');
          bit.style.setProperty('--rot', Math.floor(Math.random() * 360) + 'deg');
          bit.style.width = sz.toFixed(1) + 'px';
          bit.style.height = sz.toFixed(1) + 'px';
          bit.style.background = colors[i % colors.length];
          bit.style.borderRadius = i % 3 === 0 ? '50%' : '2px';
          bit.style.animationDelay = (Math.random() * 0.08).toFixed(2) + 's';
          host.appendChild(bit);
        }
        setTimeout(function () { host.innerHTML = ''; }, 1100);
      });
    }

    _flashToast(kind, vars) {
      var template;
      if (kind === 'tier_unlocked') {
        template = copy('toast_tier_unlocked', 'Nice - __PCT__% off unlocked!');
      } else if (kind === 'inventory_clamped') {
        template = copy('toast_inventory_clamped', "Only __ADDED__ of __TITLE__ in stock - we added __ADDED__ to your set (you asked for __REQUESTED__).");
      } else if (kind === 'inventory_none') {
        template = copy('toast_inventory_none', "__TITLE__ is out of stock for your set.");
      } else {
        template = copy('toast_added', '__TITLE__ added to your set');
      }
      var msg = template
        .replace('__TITLE__', (vars && vars.title) || '')
        .replace('__PCT__', String((vars && vars.pct) || ''))
        .replace(/__ADDED__/g, String((vars && vars.added) || 0))
        .replace('__REQUESTED__', String((vars && vars.requested) || 0));
      this._flashToastRaw(msg);
      return;
    }

    /**
     * Render an already-formatted message in the same toast surface as
     * _flashToast. Used when the source is a server response and there is
     * no locale template to interpolate (e.g. the cart-store passes through
     * Shopify's own "Only N items added due to availability" text).
     */
    _flashToastRaw(msg) {

      // Visual toast - reuse the toast element rendered in sections/build-set.liquid.
      var toast = this.parentNode && this.parentNode.querySelector('[data-bas-toast]');
      if (!toast) toast = document.querySelector('[data-bas-toast]');
      if (toast) {
        toast.textContent = msg;
        toast.hidden = false;
        toast.classList.add('is-visible');
        clearTimeout(this._toastTimer);
        var self = this;
        this._toastTimer = setTimeout(function () {
          toast.classList.remove('is-visible');
          setTimeout(function () { toast.hidden = true; }, 280);
        }, 2400);
      }

      // a11y - reuse cart-live-status polite region (per Q6).
      var live = document.getElementById('cart-live-status');
      if (live) live.textContent = msg;
    }

    // ---- checkout (commit set to Shopify cart) ---------------------------

    _checkout() {
      if (this._state.set.length === 0) return;
      if (!window.cartStore || typeof window.cartStore.addBatch !== 'function') {
        console.error('[build-set] cartStore.addBatch unavailable');
        return;
      }
      var items = this._state.set.map(function (it) {
        return {
          id: it.variantId,
          quantity: it.qty,
        };
      });

      var self = this;
      this.querySelectorAll('[data-bas-summary-body]').forEach(function (el) {
        el.classList.add('is-loading');
      });

      window.cartStore.addBatch(items).then(function () {
        self._state.set = [];
        self._renderSummary();
        self._updateStep();
        self._closeMobileDrawer();
        self.querySelectorAll('[data-bas-summary-body]').forEach(function (el) {
          el.classList.remove('is-loading');
        });
        document.dispatchEvent(new CustomEvent('cart:open'));
      }).catch(function (err) {
        self.querySelectorAll('[data-bas-summary-body]').forEach(function (el) {
          el.classList.remove('is-loading');
        });
        // Partial-success path: Shopify's /cart/add.js returns 422 with
        // "Only N items were added to your cart due to availability." when
        // the cart was actually mutated server-side but couldn't accept the
        // full requested qty. The local BAS set MUST clear in this case -
        // otherwise the user's next click sends the same items again,
        // doubling the cart on stores where the inventory ceiling isn't
        // strictly enforced. Treat it like a successful commit for state
        // cleanup (set cleared, drawer opened) while still surfacing the
        // server's message so the user understands what landed.
        var rawMsg = err && (err.message || err.detail) || '';
        var isPartial = /added to your cart due to availability/i.test(rawMsg);
        if (isPartial) {
          self._state.set = [];
          self._renderSummary();
          self._updateStep();
          self._closeMobileDrawer();
          document.dispatchEvent(new CustomEvent('cart:open'));
        }
        self._flashToastRaw(rawMsg || copy('toast_commit_failed', "We couldn't add your set - inventory may have changed. Please review your set."));
      });
    }

    // ---- collection selection --------------------------------------------

    _selectCollection(handle, fromModal) {
      this._state.activeCollection = handle || '';

      // Sync the active state across BOTH the inline chip rail and modal chips.
      var nodes = this.querySelectorAll('[data-collection-handle]');
      nodes.forEach(function (el) {
        var match = (el.dataset.collectionHandle || '') === handle;
        el.classList.toggle('is-selected', match);
        el.setAttribute('aria-selected', match ? 'true' : 'false');
      });

      if (fromModal) this._closeAllModal();
      this._applyFilter();
      this._applySort();
      this._updateStep();
    }

    // ---- search & sort ---------------------------------------------------

    _setQuery(value) {
      this._state.query = value;
      var input = this.querySelector('[data-bas-search]');
      if (input && input.value !== value) input.value = value;
      var clearBtn = this.querySelector('[data-action="clear-search"]');
      if (clearBtn) clearBtn.hidden = !value;
      this._applyFilter();
    }

    _setSort(value) {
      this._state.sort = value;
      this._applySort();
    }

    // ---- filter + sort + count -------------------------------------------

    _applyFilter() {
      var handle = this._state.activeCollection;
      var q = (this._state.query || '').trim().toLowerCase();
      var cards = this.querySelectorAll('.bas-card');
      var visibleCount = 0;
      cards.forEach(function (card) {
        var handles = (card.dataset.collectionHandles || '').split(',');
        var titleEl = card.querySelector('.bas-card__title');
        var title = (titleEl && titleEl.textContent || '').toLowerCase();
        var matchesCollection = !handle || handles.indexOf(handle) !== -1;
        var matchesQuery = !q || title.indexOf(q) !== -1;
        var visible = matchesCollection && matchesQuery;
        card.hidden = !visible;
        if (visible) visibleCount++;
      });

      // active collection title + product count
      var titleEl = this.querySelector('[data-bas-active-collection-title]');
      if (titleEl) {
        if (handle) {
          var sel = this.querySelector('.bas-chip[data-collection-handle="' + CSS.escape(handle) + '"] .bas-chip__label');
          titleEl.textContent = sel ? sel.textContent.trim() : handle;
        } else {
          titleEl.textContent = copy('chip_all', 'All');
        }
      }
      var countEl = this.querySelector('[data-bas-product-count]');
      if (countEl) {
        var template = visibleCount === 1
          ? copy('product_count_one', '1 product')
          : copy('product_count_other', '__COUNT__ products');
        countEl.textContent = template.replace('__COUNT__', String(visibleCount));
      }

      // empty state
      var grid = this.querySelector('[data-bas-grid]');
      var empty = this.querySelector('[data-bas-empty]');
      if (empty) empty.hidden = visibleCount > 0;
      if (grid) grid.style.display = visibleCount > 0 ? '' : 'none';
    }

    _applySort() {
      var sort = this._state.sort;
      var grid = this.querySelector('[data-bas-grid]');
      if (!grid) return;
      var lookup = {};
      (this._catalog || []).forEach(function (p) { lookup[String(p.id)] = p; });

      var cards = Array.prototype.slice.call(grid.children);
      cards.sort(function (a, b) {
        var pa = lookup[a.dataset.productId];
        var pb = lookup[b.dataset.productId];
        if (!pa || !pb) return 0;
        switch (sort) {
          case 'price_asc':
            return (pa.from_price || 0) - (pb.from_price || 0);
          case 'price_desc':
            return (pb.from_price || 0) - (pa.from_price || 0);
          case 'top_rated':
            return Number(b.dataset.productRating || 0) - Number(a.dataset.productRating || 0);
          case 'bestselling':
            return Number(b.dataset.productReviews || 0) - Number(a.dataset.productReviews || 0);
          case 'featured':
          default:
            return 0;  // keep insertion order
        }
      });
      cards.forEach(function (c) { grid.appendChild(c); });
    }

    // ---- step bar --------------------------------------------------------

    _updateStep() {
      var bar = this.querySelector('[data-bas-step-bar]');
      if (!bar) return;
      // Mirrors BuildSetPage.jsx:802 - default is step 2 (Browse) because
      // "All" is preselected on load, so step 1 ("Choose collection") is
      // never visited. Step bumps to 3 when a product is being customized
      // and to 4 when at least one item is in the set.
      var step = 2;
      if (this._state.selectedProductId) step = 3;
      else if (this._state.set.length > 0) step = 4;
      bar.setAttribute('data-bas-step', String(step));
    }

    // ---- chip rail overflow detection ------------------------------------

    _setupChipRail() {
      var rail = this.querySelector('[data-bas-chips-rail]');
      var wrap = this.querySelector('[data-bas-chips-rail-wrap]');
      if (!rail || !wrap) return;
      this._railEl = rail;
      this._railWrapEl = wrap;
      rail.addEventListener('scroll', this._updateChipRailOverflow, { passive: true });
      window.addEventListener('resize', this._updateChipRailOverflow);
      // Defer first measure to give layout time to settle.
      setTimeout(this._updateChipRailOverflow, 120);
    }

    _updateChipRailOverflow() {
      var rail = this._railEl;
      var wrap = this._railWrapEl;
      if (!rail || !wrap) return;
      var canLeft = rail.scrollLeft > 4;
      var canRight = rail.scrollLeft + rail.clientWidth < rail.scrollWidth - 4;
      var leftFade = wrap.querySelector('[data-bas-chips-fade="left"]');
      var rightFade = wrap.querySelector('[data-bas-chips-fade="right"]');
      var leftArrow = wrap.querySelector('.bas-arrow--left');
      var rightArrow = wrap.querySelector('.bas-arrow--right');
      if (leftFade) leftFade.hidden = !canLeft;
      if (rightFade) rightFade.hidden = !canRight;
      if (leftArrow) leftArrow.hidden = !canLeft;
      if (rightArrow) rightArrow.hidden = !canRight;
    }

    _nudgeRail(dx) {
      if (this._railEl) this._railEl.scrollBy({ left: dx, behavior: 'smooth' });
    }

    // ---- all-collections modal -------------------------------------------

    _openAllModal() {
      var modal = this.querySelector('[data-bas-all-collections-modal]');
      if (!modal) return;
      modal.hidden = false;
      this._previousFocus = document.activeElement;
      var search = modal.querySelector('[data-bas-all-search]');
      if (search) {
        search.value = '';
        this._filterModalChips('');
        requestAnimationFrame(function () { search.focus(); });
      }
      document.documentElement.style.overflow = 'hidden';
    }

    _closeAllModal() {
      var modal = this.querySelector('[data-bas-all-collections-modal]');
      if (!modal) return;
      modal.hidden = true;
      document.documentElement.style.overflow = '';
      if (this._previousFocus && typeof this._previousFocus.focus === 'function') {
        this._previousFocus.focus();
      }
    }

    _filterModalChips(q) {
      var lower = (q || '').trim().toLowerCase();
      var modal = this.querySelector('[data-bas-all-collections-modal]');
      if (!modal) return;
      var chips = modal.querySelectorAll('[data-bas-all-chip]');
      var visibleCount = 0;
      chips.forEach(function (chip) {
        var labelEl = chip.querySelector('.bas-chip__label');
        var label = labelEl ? labelEl.textContent.toLowerCase() : '';
        var visible = !lower || label.indexOf(lower) !== -1;
        chip.hidden = !visible;
        if (visible) visibleCount++;
      });
      var emptyEl = modal.querySelector('[data-bas-all-empty]');
      if (emptyEl) {
        if (visibleCount > 0) {
          emptyEl.hidden = true;
          emptyEl.textContent = '';
        } else {
          emptyEl.hidden = false;
          var template = copy('no_collections_match', 'No collections match "__QUERY__".');
          emptyEl.textContent = template.replace('__QUERY__', q || '');
        }
      }
    }
  }

  customElements.define('build-set-configurator', BuildSetConfigurator);
})();
